import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Paper, Switch, FormControlLabel,
  Button, Chip, Divider, Stack, CircularProgress, Card,
  CardContent, IconButton
} from '@mui/material';
import {
  AutoFixHigh as WorkflowIcon,
  Add as AddIcon,
  MoreVert as MoreIcon
} from '@mui/icons-material';
import apiClient from '../api/client';
import { useTenant } from '../contexts/TenantContext';

interface Workflow {
  id: string;
  title: string;
  description: string;
  triggerEvent: string;
  conditionSummary: string;
  actionsSummary: string;
  isPublished: boolean;
  executionCount: number;
  successRate: number;
  createdAt: number;
}

const Workflows: React.FC = () => {
  const { organizationId, workspaceId } = useTenant();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    setWorkflows([]);
    if (!organizationId || !workspaceId) { setLoading(false); return; }
    const refresh = async () => {
      try {
        const response = await apiClient.get<Workflow[]>('/api/v1/tenant-profile/workflows', {
          headers: { 'X-Organization-ID': organizationId, 'X-Workspace-ID': workspaceId }
        });
        if (active) setWorkflows([...response.data].sort((a,b) => b.createdAt-a.createdAt));
      } catch { if (active) setWorkflows([]); }
      finally {
        if (active) { setLoading(false); timer = setTimeout(refresh, 25000); }
      }
    };
    void refresh();
    return () => { active = false; clearTimeout(timer); };
  }, [organizationId, workspaceId]);

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WorkflowIcon fontSize="large" color="primary" />
          <Typography variant="h4" fontWeight="bold">Workflow Automation</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}>Create Workflow</Button>
      </Box>

      <Grid container spacing={3}>
        {workflows.map((wf) => (
          <Grid item xs={12} md={6} key={wf.id}>
            <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider', position: 'relative' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">{wf.title}</Typography>
                  <IconButton size="small"><MoreIcon /></IconButton>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>{wf.description}</Typography>

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>WHEN</Typography>
                    <Chip label={wf.triggerEvent} size="small" variant="outlined" color="primary" />
                  </Box>

                  <Box>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>THEN</Typography>
                    <Typography variant="body2" sx={{ bgcolor: 'rgba(255,255,255,0.03)', p: 1, borderRadius: 1 }}>
                      {wf.actionsSummary}
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Stack direction="row" spacing={3}>
                    <Box>
                      <Typography variant="caption" display="block">Executions</Typography>
                      <Typography variant="subtitle2" fontWeight="bold">{wf.executionCount}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" display="block">Success Rate</Typography>
                      <Typography variant="subtitle2" fontWeight="bold" color="success.main">{wf.successRate}%</Typography>
                    </Box>
                  </Stack>

                  <FormControlLabel
                    control={<Switch checked={wf.isPublished} color="primary" />}
                    label={wf.isPublished ? "Active" : "Paused"}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {workflows.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'transparent', border: '2px dashed', borderColor: 'divider' }}>
              <WorkflowIcon sx={{ fontSize: 60, color: 'divider', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">No Workflows Configured</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Automate lead distribution, WhatsApp replies, and SLA escalations.
              </Typography>
              <Button variant="outlined" startIcon={<AddIcon />}>Get Started</Button>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Workflows;
